import * as React from "react";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// The confirmation shell every Screen-21 action opens (Story 11.8 AC7). One
// component so the three (soon four) actions cannot drift into three different
// confirmation experiences — and so 11.12's Delete mounts into a dialog that has
// already been exercised by Disable rather than shipping an untested fourth copy.
//
// Two invariants this file owns:
//
//  • CANCEL, ESCAPE AND OVERLAY-CLICK ARE TRUE NO-OPS. `onConfirm` is reachable
//    ONLY from the confirm button; every dismissal path runs `onOpenChange(false)`
//    and nothing else. There is no "confirm on close" shortcut to get wrong.
//  • THE MUST-READ SENTENCE IS ITS OWN `Banner`, never description prose. It is the
//    part the admin most needs to have read, and burying it in a paragraph is how
//    "I didn't realise that was permanent" happens (`EditMemberDialog.tsx:526-531`).
//
// Radix supplies the focus trap, focus restore, `aria-modal`, Escape and the
// title/description wiring — this is the "shadcn primitives first" rule, not a
// hand-rolled `<div role="dialog">`.

export interface DangerConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Names the affected user — the UX spec mandates it (`:832`). */
  description: string;
  /** Rendered as a `Banner` above the footer. Omit for a non-destructive confirm. */
  mustRead?: React.ReactNode;
  mustReadVariant?: "warn" | "danger" | "info";
  confirmLabel: string;
  /** Label while the request is in flight. */
  pendingLabel?: string;
  /** `destructive` for an action that removes access; `primary` otherwise. */
  confirmVariant?: "primary" | "destructive";
  isPending?: boolean;
  /**
   * Holds Confirm shut while the dialog's own children are incomplete.
   *
   * Separate from `isPending`, which means "this request is in flight": folding
   * the two would make a dialog that has not been filled in look like one that
   * is already working, and `pendingLabel` would swap the button's text to say
   * so.
   *
   * TWO call-sites pass it, and both have an editable field the confirm cannot
   * be honoured without:
   *
   *   • `AttachToOrganizationDialog` (22.1) — `!organizationId`. Organization is
   *     required and has no default.
   *   • `ReverseAttachmentDialog` (22.3) — `!reversible || !hasReason`. Reason is
   *     required, and the preflight's eligibility is the other half.
   *
   * The remaining five — `EnablePaidAccountDialog`, `DeleteUserDialog`,
   * `ChangePasswordDialog` and the two in `DisableAccessDialog` — carry no
   * editable required field and pass nothing, so Confirm stays live for them
   * exactly as it shipped.
   */
  confirmDisabled?: boolean;
  /**
   * Native `title` on the Confirm button. A disabled button fires no events, so
   * a dialog that disables Confirm owes the administrator the reason somewhere
   * a pointer can reach it; Screen 39's field contract asks for it by name on
   * the reversal's closed-window state. Omitted by every other call-site.
   */
  confirmTitle?: string;
  onConfirm: () => void;
  /** Optional extra controls (e.g. the disable reason field). */
  children?: React.ReactNode;
}

export function DangerConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  mustRead,
  mustReadVariant = "warn",
  confirmLabel,
  pendingLabel,
  confirmVariant = "destructive",
  isPending = false,
  confirmDisabled = false,
  confirmTitle,
  onConfirm,
  children,
}: DangerConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The narrower confirm width, verbatim from the shipped call-site so every
          confirmation on the platform is the same size.

          Escape and overlay-click are SEALED while the request is in flight, for
          the same reason Cancel below is `disabled={isPending}`. Every host that
          opens this dialog renders it conditionally, so a dismissal mid-flight
          unmounts the host — and TanStack Query v5 drops a mutation's SCOPED
          `onSuccess`/`onError` when its observer unmounts. The admin would then
          see neither the refusal banner nor the success confirmation for an
          action the server had already carried out. */}
      <DialogContent
        className="w-[min(var(--width-md,520px),100%)]"
        onEscapeKeyDown={(event) => {
          if (isPending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {mustRead ? <Banner variant={mustReadVariant}>{mustRead}</Banner> : null}
        {children}
        <DialogFooter>
          {/* Dismissal only. No request is issued on this path. */}
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isPending || confirmDisabled}
            title={confirmTitle}
          >
            {isPending && pendingLabel ? pendingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
